import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import SpacesIcon from '@atlaskit/icon-lab/core/spaces';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import IconTile from '@atlaskit/icon/icon-tile';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import { analyticsAttributes } from '../../constants';

import { messages } from './messages';

const styles = cssMap({
	container: {
		display: 'flex',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		alignItems: 'center',
		alignSelf: 'stretch',
		gap: token('space.150'),
		borderBottom: `${token('border.width')} solid ${token('color.border')}`,
	},
	container_collapsed: {
		borderBottom: 'none',
	},
	iconHeadingRow: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
		flex: '1 0 0',
	},
	headingWrapper: {
		textAlign: 'center',
	},
	expandToggleWrap: {
		marginInlineStart: 'auto',
	},
});

export type TaskCardHeaderProps = {
	title: string;
	isExpanded: boolean;
	onToggle: () => void;
};

export const TaskCardHeader = ({
	title,
	isExpanded,
	onToggle,
}: TaskCardHeaderProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	const ExpandCollapseIcon = isExpanded ? ChevronDownIcon : ChevronRightIcon;

	const handleToggleClick = useCallback(
		(_: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			onToggle();
			sendAnalyticsEvent(
				analyticsEvent.update({
					action: isExpanded ? 'collapsed' : 'expanded',
					actionSubject: 'iconButton',
					actionSubjectId: 'jsmSolutionComposerPlanTaskCardHeaderToggle',
					attributes: analyticsAttributes,
				}),
			);
		},
		[onToggle, sendAnalyticsEvent, isExpanded],
	);

	return (
		<Flex xcss={cx(styles.container, !isExpanded && styles.container_collapsed)}>
			<Flex xcss={styles.iconHeadingRow}>
				<IconTile icon={SpacesIcon} label="" appearance="gray" size="medium" />
				<Box xcss={styles.headingWrapper}>
					<Heading size="xsmall" color="color.text">
						{title}
					</Heading>
				</Box>
			</Flex>
			<Box xcss={styles.expandToggleWrap}>
				<IconButton
					icon={ExpandCollapseIcon}
					label={formatMessage(isExpanded ? messages.collapseSection : messages.expandSection)}
					appearance="subtle"
					spacing="compact"
					onClick={handleToggleClick}
					testId="jsm-solution-composer-plan-task-card.header-expand-toggle"
				/>
			</Box>
		</Flex>
	);
};
